<template>
  <div class="card skill-card mb-4">
    <div class="card-body">
      <div class="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between mb-3">
        <div class="pr-lg-3 mb-3 mb-lg-0">
          <h4 class="skill-title mb-1">
            {{ typeSkill.name }}
            <span v-if="evaluationStatus === 'pass'" class="text-danger">*</span>
          </h4>
          <div class="skill-subtitle">Chấm điểm và nhận xét cho tiêu chí này</div>
        </div>

        <div class="skill-rate-wrapper">
          <el-rate
            class="admin-rate"
            size="large"
            :max="totalScore"
            :show-score="true"
            :value="skillScore"
            :disabled="evaluationStatus !== 'pass'"
            allow-half
            @change="handleScoreChange"
          />
          <div v-if="scoreError" class="invalid-feedback d-block mt-2">
            {{ scoreError }}
          </div>
        </div>
      </div>

      <div class="form-group mb-4">
        <label class="field-label mb-2">Nhận xét chung</label>

        <div>
          <div v-if="hasMainAudio" class="audio-box">
            <div class="d-flex align-items-center justify-content-between flex-wrap">
              <div class="audio-box__content">
                <div class="audio-box__label">
                  <img :src="successIcon" alt="success" class="audio-status-icon">
                  <span>Bản ghi âm nhận xét chung</span>
                </div>
                <audio controls class="audio-player">
                  <source :src="mainCommentValue">
                  Trình duyệt không hỗ trợ phát audio.
                </audio>
              </div>

              <div class="audio-box__actions">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm mr-2"
                  :disabled="evaluationStatus !== 'pass'"
                  @click="startMainCommentRecording"
                >
                  <img :src="voiceIcon" alt="voice" class="voice-icon mr-1">
                  Ghi âm lại
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="deleteRecording(null)"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>

          <div v-else class="comment-input-wrap">
            <el-input
              :value="mainCommentValue"
              class="admin-textarea"
              :autosize="{ minRows: 3, maxRows: 5 }"
              type="textarea"
              placeholder="Nhập nhận xét chung"
              :disabled="evaluationStatus !== 'pass'"
              @input="handleMainCommentInput"
              @focus="touchMainComment"
              @blur="touchMainComment"
            />

            <button
              type="button"
              class="btn btn-light comment-voice-btn"
              :class="{ recording: isRecording && currentRecordingField === typeSkill.key && currentRecordingIndex === null }"
              :disabled="evaluationStatus !== 'pass'"
              @click="startMainCommentRecording"
            >
              <img :src="voiceIcon" alt="voice recording" class="voice-icon">
              <span>{{ isRecording && currentRecordingField === typeSkill.key && currentRecordingIndex === null ? 'Đang ghi âm' : 'Ghi âm' }}</span>
            </button>
          </div>

          <div v-if="mainCommentError" class="invalid-feedback d-block mt-2">
            {{ mainCommentError }}
          </div>
        </div>
      </div>

      <div class="detail-section">
        <div class="detail-heading mb-3">Lỗi và đề xuất cải thiện</div>

        <div v-for="(item, index) in homeworkData" :key="item.outcomes_id || index" class="detail-item">
          <div class="detail-item__title">
            Bài {{ index + 1 }}
          </div>

          <div>
            <div v-if="hasDetailedAudio(item.outcomes_id)" class="audio-box">
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="audio-box__content">
                  <div class="audio-box__label">
                    <img :src="successIcon" alt="success" class="audio-status-icon">
                    <span>Bản ghi âm bài {{ index + 1 }}</span>
                  </div>
                  <audio controls class="audio-player">
                    <source :src="getDetailedComment(item.outcomes_id)">
                    Trình duyệt không hỗ trợ phát audio.
                  </audio>
                </div>

                <div class="audio-box__actions">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm mr-2"
                    :disabled="evaluationStatus !== 'pass'"
                    @click="startDetailedCommentRecording(item.outcomes_id)"
                  >
                    <img :src="voiceIcon" alt="voice" class="voice-icon mr-1">
                    Ghi âm lại
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="deleteRecording(item.outcomes_id)"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="comment-input-wrap">
              <el-input
                :value="getDetailedComment(item.outcomes_id)"
                class="admin-textarea"
                :autosize="{ minRows: 2, maxRows: 4 }"
                type="textarea"
                :placeholder="`Nhập lỗi và đề xuất cải thiện bài ${index + 1}`"
                :disabled="evaluationStatus !== 'pass'"
                @input="handleDetailedCommentInput(item.outcomes_id, $event)"
                @focus="touchDetailedComment(item.outcomes_id)"
                @blur="touchDetailedComment(item.outcomes_id)"
              />

              <button
                type="button"
                class="btn btn-light comment-voice-btn comment-voice-btn--detail"
                :class="{ recording: isRecording && currentRecordingIndex === item.outcomes_id }"
                :disabled="evaluationStatus !== 'pass'"
                @click="startDetailedCommentRecording(item.outcomes_id)"
              >
                <img :src="voiceIcon" alt="voice recording" class="voice-icon">
                <span>{{ isRecording && currentRecordingIndex === item.outcomes_id ? 'Đang ghi âm' : 'Ghi âm' }}</span>
              </button>
            </div>

            <div v-if="getDetailedCommentError(item.outcomes_id)" class="invalid-feedback d-block mt-2">
              {{ getDetailedCommentError(item.outcomes_id) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import voiceIcon from '@/assets/img/voice.png'
import successIcon from '@/assets/img/success-icon.svg'

export default {
  name: 'SkillOfSpeaking',
  props: {
    currentHomework: {
      type: Object,
      default: function () {
        return {}
      }
    },
    typeSkill: {
      type: Object,
      default: function () {
        return {}
      }
    },
    typeCourse: {
      type: Number,
      default: 0
    },
    evaluationStatus: {
      type: String,
      default: 'pass'
    },
    forceValidate: {
      type: Boolean,
      default: false
    },
    homeworkData: {
      type: Array,
      default: function () {
        return []
      }
    },
    totalScore: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      voiceIcon: voiceIcon,
      successIcon: successIcon,
      isRecording: false,
      currentRecordingField: null,
      currentRecordingIndex: null,
      mediaRecorder: null,
      audioChunks: [],
      recordingStartTime: null,
      maxFileSize: 5 * 1024 * 1024,
      minDuration: 10,
      scoreTouched: false,
      mainCommentTouched: false,
      detailedCommentTouched: {}
    }
  },
  computed: {
    skillData() {
      if (!this.currentHomework[this.typeSkill.key]) {
        this.$set(this.currentHomework, this.typeSkill.key, {
          score: null,
          comment: '',
          detailedComments: {}
        })
      }

      if (!this.currentHomework[this.typeSkill.key].detailedComments) {
        this.$set(this.currentHomework[this.typeSkill.key], 'detailedComments', {})
      }

      return this.currentHomework[this.typeSkill.key] ?? 0
    },
    scoreFieldName() {
      return 'Điểm ' + (this.typeSkill.name || '')
    },
    mainCommentFieldName() {
      return 'Nhận xét ' + (this.typeSkill.name || '')
    },
    skillScore() {
      return this.skillData.score === null || this.skillData.score === undefined ? 0 : this.skillData.score
    },
    mainCommentValue() {
      return this.skillData.comment || ''
    },
    hasMainAudio() {
      return this.mainCommentValue && this.mainCommentValue.indexOf('blob:') === 0
    },
    scoreError() {
      if (!this.scoreTouched || this.evaluationStatus !== 'pass') {
        return ''
      }
      if (this.skillData.score === null || this.skillData.score === undefined || this.skillData.score === '') {
        return this.scoreFieldName + ' là bắt buộc'
      }
      return ''
    },
    mainCommentError() {
      if (!this.mainCommentTouched || this.evaluationStatus !== 'pass') {
        return ''
      }
      return this.validateMainComment(this.mainCommentValue)
    }
  },
  watch: {
    evaluationStatus(newValue) {
      if (newValue !== 'pass') {
        this.scoreTouched = false
        this.mainCommentTouched = false
        this.detailedCommentTouched = {}
      }
    },
    forceValidate(newValue) {
      if (!newValue || this.evaluationStatus !== 'pass') {
        return
      }

      this.scoreTouched = true
      this.mainCommentTouched = true

      this.homeworkData.forEach((item) => {
        if (this.getDetailedComment(item.outcomes_id)) {
          this.$set(this.detailedCommentTouched, item.outcomes_id, true)
        }
      })
    }
  },
  beforeDestroy() {
    if (this.isRecording && this.mediaRecorder) {
      this.mediaRecorder.stop()
    }
  },
  methods: {
    validateMainComment(value) {
      if (this.evaluationStatus !== 'pass') {
        return ''
      }

      if (!value || value.trim() === '') {
        return 'Vui lòng nhập nhận xét chung'
      }

      if (value.indexOf('blob:') === 0) {
        return ''
      }

      if (value.trim().length < 50) {
        return 'Nhận xét phải có ít nhất 50 ký tự'
      }

      return ''
    },
    validateDetailedComment(value) {
      if (!value) {
        return ''
      }

      if (value.indexOf('blob:') === 0) {
        return ''
      }

      if (value.length < 50) {
        return 'Nhận xét phải có ít nhất 50 ký tự'
      }

      return ''
    },
    touchScore() {
      this.scoreTouched = true
    },
    touchMainComment() {
      this.mainCommentTouched = true
    },
    touchDetailedComment(outcomesId) {
      this.$set(this.detailedCommentTouched, outcomesId, true)
    },
    getDetailedCommentError(outcomesId) {
      if (!this.detailedCommentTouched[outcomesId]) {
        return ''
      }
      return this.validateDetailedComment(this.getDetailedComment(outcomesId))
    },
    handleScoreChange(value) {
      this.touchScore()
      this.$set(this.skillData, 'score', value)
    },
    handleMainCommentInput(value) {
      this.touchMainComment()
      this.$set(this.skillData, 'comment', value)
    },
    handleDetailedCommentInput(outcomesId, value) {
      this.touchDetailedComment(outcomesId)
      this.setDetailedComment(outcomesId, value)
    },
    ensureDetailedComments() {
      if (!this.skillData.detailedComments) {
        this.$set(this.skillData, 'detailedComments', {})
      }
    },
    getDetailedComment(outcomesId) {
      this.ensureDetailedComments()
      return this.skillData.detailedComments[outcomesId] || ''
    },
    setDetailedComment(outcomesId, value) {
      this.ensureDetailedComments()
      this.$set(this.skillData.detailedComments, outcomesId, value)
    },
    hasDetailedAudio(outcomesId) {
      const value = this.getDetailedComment(outcomesId)
      return value && value.indexOf('blob:') === 0
    },
    cleanupBlobUrl(outcomesId) {
      if (!this.currentHomework || !this.currentHomework[this.typeSkill.key]) {
        return
      }

      const skillData = this.currentHomework[this.typeSkill.key]

      if (outcomesId !== null && outcomesId !== undefined) {
        if (skillData.detailedComments && skillData.detailedComments[outcomesId]) {
          const detailUrl = skillData.detailedComments[outcomesId]
          if (detailUrl && detailUrl.indexOf('blob:') === 0) {
            URL.revokeObjectURL(detailUrl)
          }
          this.$delete(skillData.detailedComments, outcomesId)
        }
        return
      }

      if (skillData.comment && skillData.comment.indexOf('blob:') === 0) {
        URL.revokeObjectURL(skillData.comment)
      }
      this.$set(skillData, 'comment', '')
    },
    setBlobUrl(audioUrl, outcomesId) {
      if (outcomesId !== null && outcomesId !== undefined) {
        this.ensureDetailedComments()
        const existingUrl = this.skillData.detailedComments[outcomesId]
        if (existingUrl && existingUrl.indexOf('blob:') === 0 && existingUrl !== audioUrl) {
          URL.revokeObjectURL(existingUrl)
        }
        this.touchDetailedComment(outcomesId)
        this.$set(this.skillData.detailedComments, outcomesId, audioUrl)
        return
      }

      const existingUrl = this.skillData.comment
      if (existingUrl && existingUrl.indexOf('blob:') === 0 && existingUrl !== audioUrl) {
        URL.revokeObjectURL(existingUrl)
      }
      this.touchMainComment()
      this.$set(this.skillData, 'comment', audioUrl)
    },
    getRecordedMimeType() {
      if (this.mediaRecorder && this.mediaRecorder.mimeType) {
        return this.mediaRecorder.mimeType
      }

      if (this.audioChunks.length && this.audioChunks[0].type) {
        return this.audioChunks[0].type
      }

      return 'audio/webm'
    },
    resetRecordingState(stream) {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      this.audioChunks = []
      this.isRecording = false
      this.currentRecordingField = null
      this.currentRecordingIndex = null
      this.recordingStartTime = null
    },
    handleRecordingFailure(errorMessage, stream, outcomesId) {
      this.$message.error(errorMessage)
      this.cleanupBlobUrl(outcomesId)
      this.resetRecordingState(stream)
    },
    startVoiceRecording(fieldKey, outcomesId) {
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        this.$message.error('Trình duyệt không hỗ trợ ghi âm. Vui lòng sử dụng Chrome, Firefox hoặc Edge.')
        return
      }

      if (this.evaluationStatus !== 'pass') {
        return
      }

      if (this.isRecording) {
        this.stopVoiceRecording()
        return
      }

      this.touchScore()
      if (outcomesId !== null && outcomesId !== undefined) {
        this.touchDetailedComment(outcomesId)
      } else {
        this.touchMainComment()
      }

      this.$message.info('Đang yêu cầu quyền truy cập microphone...')
      this.cleanupBlobUrl(outcomesId)

      navigator.mediaDevices.getUserMedia({ video: false, audio: true })
        .then((stream) => {
          this.mediaRecorder = new MediaRecorder(stream)
          this.audioChunks = []
          this.recordingStartTime = Date.now()

          this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              this.audioChunks.push(event.data)
            }
          }

          this.mediaRecorder.onstop = () => {
            const recordingDuration = (Date.now() - this.recordingStartTime) / 1000

            if (recordingDuration < this.minDuration) {
              this.handleRecordingFailure(
                'Thời lượng ghi âm tối thiểu ' + this.minDuration + ' giây. Bạn đã ghi âm được ' + Math.round(recordingDuration) + ' giây.',
                stream,
                outcomesId
              )
              return
            }

            const audioBlob = new Blob(this.audioChunks, { type: this.getRecordedMimeType() })

            if (audioBlob.size > this.maxFileSize) {
              const fileSizeMB = (audioBlob.size / (1024 * 1024)).toFixed(2)
              this.handleRecordingFailure(
                'Dung lượng file vượt quá giới hạn 5MB (' + fileSizeMB + 'MB). Vui lòng ghi âm ngắn hơn.',
                stream,
                outcomesId
              )
              return
            }

            const audioUrl = URL.createObjectURL(audioBlob)
            this.setBlobUrl(audioUrl, outcomesId)
            this.resetRecordingState(stream)

            const fileSizeMB = (audioBlob.size / (1024 * 1024)).toFixed(2)
            this.$message.success('Ghi âm thành công! Thời lượng: ' + Math.round(recordingDuration) + 's, Dung lượng: ' + fileSizeMB + 'MB')
          }

          this.mediaRecorder.onerror = (event) => {
            console.error('MediaRecorder error:', event.error)
            this.handleRecordingFailure('Lỗi ghi âm. Vui lòng thử lại.', stream, outcomesId)
          }

          this.currentRecordingField = fieldKey
          this.currentRecordingIndex = outcomesId === undefined ? null : outcomesId
          this.isRecording = true
          this.mediaRecorder.start()
          this.$message.info('Đang ghi âm... Hãy nói rõ ràng.')
        })
        .catch((err) => {
          console.error('Microphone access error:', err)
          const errorMessages = {
            NotAllowedError: 'Bạn cần cho phép truy cập microphone để sử dụng tính năng ghi âm.',
            PermissionDeniedError: 'Bạn cần cho phép truy cập microphone để sử dụng tính năng ghi âm.',
            NotFoundError: 'Không tìm thấy microphone. Vui lòng kiểm tra thiết bị.',
            NotReadableError: 'Microphone đang được sử dụng bởi ứng dụng khác. Vui lòng thử lại.'
          }
          this.$message.error(errorMessages[err.name] || 'Lỗi truy cập microphone. Vui lòng thử lại.')
          this.resetRecordingState()
        })
    },
    stopVoiceRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
      }
    },
    deleteRecording(outcomesId) {
      this.cleanupBlobUrl(outcomesId)
      if (outcomesId !== null && outcomesId !== undefined) {
        this.touchDetailedComment(outcomesId)
      } else {
        this.touchMainComment()
      }
      this.$message.success('Đã xóa bản ghi âm.')
    },
    startMainCommentRecording() {
      this.startVoiceRecording(this.typeSkill.key, null)
    },
    startDetailedCommentRecording(outcomesId) {
      this.startVoiceRecording(this.typeSkill.key, outcomesId)
    }
  }
}
</script>

<style lang="scss" scoped>
.skill-card {
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1.5rem rgba(140, 152, 164, 0.08);
}

.skill-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #181c32;
}

.skill-subtitle {
  font-size: 0.9375rem;
  color: #7e8299;
}

.skill-rate-wrapper {
  min-width: 280px;
}

.field-label,
.detail-heading,
.detail-item__title {
  font-size: 0.975rem;
  font-weight: 600;
  color: #3f4254;
}

.detail-section {
  border-top: 1px solid #eff2f5;
  padding-top: 1.25rem;
}

.detail-item {
  padding: 1rem;
  margin-bottom: 1rem;
  background: #f8f9fc;
  border: 1px solid #eef0f8;
  border-radius: 0.75rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item__title {
  margin-bottom: 0.75rem;
}

.comment-input-wrap {
  position: relative;
}

.comment-voice-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.75rem;
  border-color: #e4e6ef;
  color: #3f4254;
  font-size: 0.95rem;
}

.comment-voice-btn.recording {
  background: #fff4f4;
  border-color: #f64e60;
  color: #f64e60;
}

.comment-voice-btn--detail {
  margin-top: 0.625rem;
}

.voice-icon {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}

.audio-box {
  padding: 1rem 1.25rem;
  background: #f8f9fc;
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
}

.audio-box__content {
  flex: 1 1 auto;
  min-width: 260px;
}

.audio-box__label {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.975rem;
  font-weight: 500;
  color: #3f4254;
}

.audio-status-icon {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

.audio-box__actions {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
}

.audio-player {
  width: 100%;
  max-width: 420px;
}

.admin-rate::v-deep .el-rate {
  display: flex;
  align-items: center;
}

.admin-rate::v-deep .el-rate__text {
  margin-left: 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: #3699ff;
}

.admin-textarea::v-deep .el-textarea__inner {
  border-color: #e4e6ef;
  border-radius: 0.475rem;
  min-height: 110px;
  padding: 0.75rem 0.875rem;
  font-size: 0.975rem;
  line-height: 1.7;
}

.admin-textarea::v-deep .el-textarea__inner:focus {
  border-color: #5867dd;
}

@media (max-width: 991.98px) {
  .skill-rate-wrapper {
    width: 100%;
    min-width: 0;
  }

  .audio-box__actions {
    width: 100%;
  }
}
</style>
